import { Component, Inject, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Watch } from '@common/decorators/watch.decorator';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import {
  USER_ACCOUNT_ACTIONS,
  USER_GROUP_ACTIONS,
  UserAccountActions,
  UserGroupActions
} from '@/app/platform/platform-data/actions/types';
import { USER_GROUP_QUERIES, UserGroupQueries } from '@/app/platform/platform-data/queries/types';
import { UserAccountEntity } from '@/app/platform/platform-data/models/user-account.entity';

@Component({
    selector: 'user-group-linked-users-editor[userGroup]',
    templateUrl: './user-group-linked-users-editor.component.html',
    styleUrls: ['./user-group-linked-users-editor.component.css'],
    standalone: false
})
export class UserGroupLinkedUsersEditorComponent implements OnInit {
  @Input() userGroup: UserGroupEntity;

  @Input('filteredUser') set filteredUserInput(v: UserAccountEntity | undefined) {
    this.filteredUser = v;
    if (v) {
      this.selectedMember = v;
    }
  }

  filteredUser?: UserAccountEntity;

  @Watch('userGroup', { immediate: true })
  onUserGroupChange(newVal: UserGroupEntity) {
    this.selectedNonMember = undefined;
  }

  constructor(
    @Inject(USER_ACCOUNT_ACTIONS)
    private readonly userAccountActions: UserAccountActions,
    @Inject(USER_GROUP_QUERIES)
    private readonly userGroupQueries: UserGroupQueries,
    @Inject(USER_GROUP_ACTIONS)
    private readonly userGroupActions: UserGroupActions
  ) {}

  readonly dropZoneId = 'NewUserDropZone';
  dropZoneEntered = false;
  loading = false;
  searchText = '';
  selectedNonMember: UserAccountEntity;

  selectedMember: UserAccountEntity;
  filterSearchText = '';

  ngOnInit(): void {
    this.loadUsers();
  }

  get groupMembers$(): Observable<UserAccountEntity[]> {
    return this.userGroupQueries.members$(this.userGroup);
  }

  get nonMembers$(): Observable<UserAccountEntity[]> {
    return this.userGroupQueries.nonMembers$(this.userGroup).pipe(
      map(users =>
        users.filter(user => {
          if (!this.searchText) {
            return true;
          }

          const filterProperties: (keyof UserAccountEntity)[] = [
            'firstName',
            'userName',
            'lastName',
            'displayName',
            'email'
          ];
          return filterProperties.some(k => {
            const propertyValue = user[k] as string;
            return propertyValue.toLowerCase().includes(this.searchText.toLowerCase());
          });
        })
      )
    );
  }

  async addMember(user: UserAccountEntity) {
    const result = await this.userGroupActions.addMember(this.userGroup, user);
    result.ifSuccess(r => {
      this.selectedNonMember = undefined;
    });
  }

  async removeMember(member: UserAccountEntity) {
    const result = await this.userGroupActions.removeMember(this.userGroup, member);
  }

  /**
   * Initialen voor de coral avatar-cirkel (Dusk Warm). De mockup toont
   * foto-avatars die de app niet heeft; we vallen terug op initialen, conform
   * het app-brede avatar-patroon (zie authenticated-user-indicator §5.5).
   */
  initials(user: UserAccountEntity): string {
    if (!user) {
      return '';
    }
    const first = (user.firstName ?? '').trim();
    const last = (user.lastName ?? '').trim();
    if (first || last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    const display = (user.displayName ?? '').trim();
    if (!display) {
      return '';
    }
    const parts = display.split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }

  private async loadUsers() {
    this.loading = true;
    await this.userAccountActions.loadAll();
    this.loading = false;
  }
}
